if (window.Maximize.checkFileNotLoaded()) {
  class CustomCollections extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.currentTab = 1;
      this.isLoading = false;
      this.cachedData = {};
      this.onSelectCollection = this.handleSelectCollection.bind(this);
      this.preventViewAllClick = this.preventViewAllClick.bind(this);
      this.viewAllDisabled = false;
      this._initialized = false;
      this._observer = null;
    }

    connectedCallback() {
      // In design mode init immediately so merchants can interact with all sections
      if (Shopify.designMode) {
        this._init();
        this._bindDesignModeListeners();
        return;
      }

      // Defer heavy init (cloneNode, querySelectorAll, event listeners) until
      // the element is within 200px of the viewport — keeps connectedCallback
      // near-instant and removes its cost from TBT.
      this._observer = new IntersectionObserver(
        (entries, obs) => {
          if (!entries[0].isIntersecting) return;
          obs.disconnect();
          this._observer = null;
          this._init();
        },
        { rootMargin: '200px 0px' }
      );
      this._observer.observe(this);
    }

    _init() {
      if (this._initialized) return;
      this._initialized = true;

      // Expose this controller so the filter/search module can return to
      // browse mode (clearSearch → loadDataCollection(1)) without duplicating
      // the tab-fetch logic.
      window.__tmCollectionControllers = window.__tmCollectionControllers || {};
      window.__tmCollectionControllers[this.sectionId] = this;

      this.collectionButtons = this.querySelectorAll('.collection-title');
      this.collectionsWrapperEl = document.getElementById(`Collections--${this.sectionId}`);
      this.viewAllLinks = this.querySelectorAll('.collection__view-all');

      // Subcategory tabs live inside the swapped content, so bind via delegation
      // on this element once. A click on a .tm-subcat-tab either loads that
      // subcollection, or — if it's already active (its × was clicked, or you
      // re-tap the active one) — returns to the full parent category.
      if (!this._subcatBound) {
        this._subcatBound = true;
        this.addEventListener('click', (event) => {
          const tab = event.target.closest('.tm-subcat-tab');
          if (!tab || !this.contains(tab)) return;
          event.preventDefault();
          const row = tab.closest('.tm-subcat-row');
          const isActive = tab.getAttribute('aria-pressed') === 'true';
          const clickedClear = !!event.target.closest('.tm-subcat-clear');
          if (isActive || clickedClear) {
            // Deselect → back to the full parent category. Prefer reloading via
            // the parent's main tab so that tab re-activates and caching works;
            // fall back to a direct URL load if we can't find the tab.
            const parentUrl = row ? row.dataset.parentUrl : '';
            var parentTabIndex = -1;
            if (parentUrl) {
              this.collectionButtons.forEach((btn, i) => {
                if (btn.dataset && btn.dataset.collectionUrl === parentUrl) parentTabIndex = i + 1;
              });
            }
            if (parentTabIndex > 0) {
              this.currentTab = -1; // force reload even if it equals last tab
              this.loadDataCollection(parentTabIndex);
              this.selectCollection(parentTabIndex);
            } else if (parentUrl) {
              this.loadByCollectionUrl(parentUrl);
            }
          } else {
            const subUrl = tab.dataset.subcatUrl;
            if (subUrl) this.loadByCollectionUrl(subUrl, subUrl);
          }
        });
      }

      if (!this.collectionsWrapperEl) return;

      // The product cards carry the theme's `anm-reveal-item` class which keeps
      // them at opacity:0.01 until a scroll IntersectionObserver flips
      // data-active="true". That observer only watches `.anm-reveal-container`
      // parents — our grid uses a different wrapper, so cards would stay
      // invisible forever. Force them visible right away (this is a shopping
      // grid; products should show immediately, no scroll-reveal needed).
      this._activateRevealItems(document.getElementById(`SupermarketGridContent--${this.sectionId}`));

      if (this.collectionButtons.length > 1) {
        this.collectionButtons.forEach((button) => {
          button.addEventListener('click', this.onSelectCollection);
        });
      }

      if (this.collectionButtons[0]) {
        // Mark tab 1 active immediately for instant visual feedback.
        this.selectCollection(1);

        // IMPORTANT: the server-rendered initial content uses the first block's
        // collection via block.settings.collection, which does NOT have
        // `.filters` populated (Shopify only computes filters in collection-page
        // context). That's why tab 1 showed products but no filter bar. To get
        // the filter bar, fetch tab 1 through the collection URL — the same path
        // tabs 2+ use — which renders the section in collection context with
        // filters. We do this once on load. The initial paint still shows the
        // products instantly (no blank flash); the filter bar appears a moment
        // later when this resolves.
        const firstButton = this.collectionButtons[0];
        const firstUrl = firstButton && firstButton.dataset ? firstButton.dataset.collectionUrl : '';
        if (firstUrl) {
          this.loadDataCollection(1);
        } else {
          // No collection URL available — keep the initial render and cache it.
          const fullContentEl = document.getElementById(`SupermarketGridContent--${this.sectionId}`);
          if (fullContentEl) {
            this.cachedData[`tabIndex-1`] = fullContentEl.innerHTML;
          }
        }
      }

      if (this.viewAllLinks.length > 0) {
        this.viewAllLinks.forEach((el) => {
          el.addEventListener('click', this.preventViewAllClick);
        });

        this.setViewAllLink(this.dataset.defaultViewAllLink);
      }
    }

    disconnectedCallback() {
      // Clean up observer if element removed before init fired
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }

      if (this._onDesignModeBlockSelect) {
        document.removeEventListener('shopify:block:select', this._onDesignModeBlockSelect);
        this._onDesignModeBlockSelect = null;
      }

      if (!this.collectionButtons) return;

      this.collectionButtons.forEach((button) => {
        button.removeEventListener('click', this.onSelectCollection);
      });

      this.viewAllLinks.forEach((el) => {
        el.removeEventListener('click', this.preventViewAllClick);
      });
    }

    // When the merchant clicks a collection block in the theme editor sidebar,
    // Shopify dispatches `shopify:block:select`. We use it to switch the preview
    // to that block's tab so the merchant sees the block they are editing.
    _bindDesignModeListeners() {
      this._onDesignModeBlockSelect = (event) => {
        if (!this.contains(event.target)) return;
        const button = event.target.closest('.collection-title');
        if (!button) return;
        if (button.classList.contains('is-active')) return;
        button.click();
      };
      document.addEventListener('shopify:block:select', this._onDesignModeBlockSelect);
    }

    async handleSelectCollection(e) {
      const tabIndex = parseInt(e.currentTarget.dataset.tabIndex, 10);
      const viewAllLink = e.currentTarget.dataset.viewAllLink;
      console.log('[TM-tab] handleSelectCollection invoked, tabIndex=', tabIndex,
        'currentTab=', this.currentTab, 'isLoading=', this.isLoading,
        'collectionsWrapperEl present=', !!this.collectionsWrapperEl);
      if (this.currentTab !== tabIndex && !this.isLoading) {
        await this.loadDataCollection(tabIndex);
        this.selectCollection(tabIndex);
        if (this.viewAllLinks.length > 0) {
          this.setViewAllLink(viewAllLink);
        }
      } else {
        console.log('[TM-tab] click ignored (same tab or already loading)');
      }
    }

    selectCollection(index) {
      this.collectionButtons.forEach((button) => {
        const tabIndex = parseInt(button.dataset.tabIndex, 10);
        if (tabIndex === index) {
          button.classList.add('is-active');
        } else {
          button.classList.remove('is-active');
        }
      });
    }

    setViewAllLink(link) {
      const isDisabled = !link;

      this.viewAllLinks.forEach((el) => {
        el.href = link || '';
        el.classList.toggle('opacity-70', isDisabled);
        el.classList.toggle('hover:cursor-not-allowed', isDisabled);
      });

      this.viewAllDisabled = isDisabled;
    }

    preventViewAllClick(e) {
      if (this.viewAllDisabled) {
        e.preventDefault();
      }
    }

    selectCollectionButtonsLoading(isLoading, nextTabIndexActive) {
      if (isLoading) {
        this.collectionButtons[nextTabIndexActive - 1].classList.add('next-tab-active');
        this.collectionButtons.forEach((button) => {
          button.setAttribute('aria-disabled', 'true');
        });
      } else {
        this.collectionButtons[nextTabIndexActive - 1].classList.remove('next-tab-active');
        this.collectionButtons.forEach((button) => {
          button.removeAttribute('aria-disabled');
        });
      }
      this.isLoading = isLoading;
    }

    async loadDataCollection(index) {
      const tabIndexKey = `tabIndex-${index}`;
      const TM = '[TM-tab]';
      console.log(TM, 'click on tab', index, 'section', this.sectionId);

      if (this.cachedData[tabIndexKey]) {
        console.log(TM, 'using cached data for tab', index);
        this._applyCachedContent(this.cachedData[tabIndexKey]);
        this.currentTab = index;
      } else {
        this.selectCollectionButtonsLoading(true, index);

        const button = this.collectionButtons[index - 1];
        const collectionUrl = button && button.dataset ? button.dataset.collectionUrl : '';
        const useCollectionUrl = !!collectionUrl;

        const url = useCollectionUrl
          ? `${collectionUrl}?section_id=${this.sectionId}`
          : `${window.location.pathname}?section_id=${this.sectionId}&page=${index}`;

        console.log(TM, 'fetching', url);

        try {
          const response = await fetch(url);
          const responseText = await response.text();
          const html = (new DOMParser()).parseFromString(responseText, 'text/html');

          const contentId = `SupermarketGridContent--${this.sectionId}`;
          const currentContent = document.getElementById(contentId);
          const newContent = html.getElementById(contentId);

          console.log(TM, 'response status', response.status, 'text length', responseText.length,
            'currentContent found:', !!currentContent, 'newContent found:', !!newContent);

          if (currentContent && newContent) {
            const newProductCount = newContent.querySelectorAll('.supermarket-grid__item').length;
            const hasFilterWrapper = !!newContent.querySelector('.facet-filter-wrapper');
            console.log(TM, 'NEW content has', newProductCount, 'products,',
              hasFilterWrapper ? 'WITH filter wrapper' : 'NO filter wrapper');

            currentContent.innerHTML = newContent.innerHTML;
            this.collectionsWrapperEl =
              document.getElementById(`Collections--${this.sectionId}`);
            this.cachedData[tabIndexKey] = newContent.innerHTML;
            this.currentTab = index;

            // CRITICAL: Alpine doesn't reliably auto-init bindings added via
            // innerHTML. Without this, the product cards stay hidden behind
            // x-cloak, xRangePrice/xProductForm/xQuickView never bootstrap,
            // and the swap looks like an empty grid. initTree() walks the
            // subtree and initializes every x-data/x-bind/x-on it finds.
            this._initAlpineSubtree(currentContent);

            // Make the freshly-injected product cards visible (they carry the
            // theme's reveal-animation class that defaults to invisible).
            this._activateRevealItems(currentContent);

            // Also execute any <script> tags in the new content. innerHTML
            // does NOT run scripts; we have to re-create + replace them.
            this._reexecuteScripts(currentContent);

            console.log(TM, 'swap done. Tab', index, 'now active.');

            document.dispatchEvent(new CustomEvent('tm:supermarket-grid:content-swapped', {
              bubbles: true,
              detail: { root: currentContent, sectionId: this.sectionId },
            }));
          } else {
            console.warn(TM, 'SupermarketGridContent IDs not found — falling back to legacy swap');
            const newProductsContent = html.getElementById(`Collections--${this.sectionId}`);
            if (newProductsContent && this.collectionsWrapperEl) {
              this.collectionsWrapperEl.innerHTML = newProductsContent.innerHTML;
              this.anmRevealItems = newProductsContent.querySelectorAll('.anm-reveal-item');
              this.anmRevealItems.forEach((item) => {
                item.classList.remove('anm-reveal-item');
              });
              this.cachedData[tabIndexKey] = newProductsContent.innerHTML;
              this.currentTab = index;
              console.log(TM, 'legacy fallback swap done');
            } else {
              console.error(TM, 'BOTH targets missing — nothing was swapped. response:',
                responseText.substring(0, 400));
            }
          }
        } catch (error) {
          console.error(TM, 'fetch/swap failed:', error);
        }

        this.selectCollectionButtonsLoading(false, index);
      }
    }

    // Load an arbitrary collection URL (used by subcategory tabs) and swap the
    // SupermarketGridContent, reusing the same Alpine/reveal/script/event
    // pipeline as loadDataCollection. Single-select: this just shows the given
    // collection's products; search/sort/filters then operate on it because
    // the section re-renders in that collection's context.
    async loadByCollectionUrl(collectionUrl, activeSubUrl) {
      const TM = '[TM-subcat]';
      if (!collectionUrl) return;
      const url = `${collectionUrl}?section_id=${this.sectionId}`;
      console.log(TM, 'fetching', url);

      const contentId = `SupermarketGridContent--${this.sectionId}`;
      const currentContent = document.getElementById(contentId);
      if (currentContent) currentContent.style.opacity = '0.5';

      // Capture the CURRENT (parent) subcategory row before swapping. The
      // section can't tell which parent a shared subcollection belongs to, so
      // after the swap we restore THIS row (the one the user actually clicked
      // from) and mark the active subcollection — preventing two categories
      // that share a subcollection from showing each other's tabs.
      let preservedSubRow = null;
      if (currentContent) {
        const liveRow = currentContent.querySelector('.tm-subcat-row');
        if (liveRow) preservedSubRow = liveRow.cloneNode(true);
      }

      try {
        const response = await fetch(url, { credentials: 'same-origin' });
        const responseText = await response.text();
        const html = (new DOMParser()).parseFromString(responseText, 'text/html');
        const newContent = html.getElementById(contentId);

        if (currentContent && newContent) {
          currentContent.innerHTML = newContent.innerHTML;
          this.collectionsWrapperEl = document.getElementById(`Collections--${this.sectionId}`);

          // Restore the parent's subcategory row (the correct one) over whatever
          // the section rendered, and set active state to the clicked sub.
          if (preservedSubRow) {
            const renderedRow = currentContent.querySelector('.tm-subcat-row');
            const restored = preservedSubRow.cloneNode(true);
            // Update active state on the restored row
            restored.querySelectorAll('.tm-subcat-tab').forEach((tab) => {
              const isActive = activeSubUrl && tab.dataset.subcatUrl === activeSubUrl;
              tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
              tab.classList.toggle('tm-subcat-tab--active', !!isActive);
              // add/remove the × clear marker
              const existingClear = tab.querySelector('.tm-subcat-clear');
              if (isActive && !existingClear) {
                const x = document.createElement('span');
                x.className = 'tm-subcat-clear inline-flex items-center justify-center leading-none';
                x.setAttribute('aria-label', 'Clear subcategory');
                x.style.fontSize = '16px';
                x.innerHTML = '&times;';
                tab.appendChild(x);
              } else if (!isActive && existingClear) {
                existingClear.remove();
              }
            });
            if (renderedRow) {
              renderedRow.replaceWith(restored);
            } else {
              // no row rendered (e.g. section resolved a block w/o subcats) —
              // insert the preserved row before the search row if present
              const searchRow = currentContent.querySelector('.tm-search-row');
              if (searchRow) searchRow.parentNode.insertBefore(restored, searchRow);
            }
          }

          this._initAlpineSubtree(currentContent);
          this._activateRevealItems(currentContent);
          this._reexecuteScripts(currentContent);
          console.log(TM, 'subcategory swap done');
          this.currentTab = -1;
          this.cachedData = {};
          document.dispatchEvent(new CustomEvent('tm:supermarket-grid:content-swapped', {
            bubbles: true,
            detail: { root: currentContent, sectionId: this.sectionId },
          }));
        } else {
          console.warn(TM, 'content IDs not found, swap skipped');
        }
      } catch (error) {
        console.error(TM, 'subcategory fetch/swap failed:', error);
      } finally {
        if (currentContent) currentContent.style.opacity = '';
      }
    }

    // Restore cached HTML for a tab. The cached value is the innerHTML
    // of the SupermarketGridContent wrapper from a previous swap.
    _applyCachedContent(html) {
      const contentEl =
        document.getElementById(`SupermarketGridContent--${this.sectionId}`);
      if (contentEl) {
        contentEl.innerHTML = html;
        this.collectionsWrapperEl =
          document.getElementById(`Collections--${this.sectionId}`);
        // Same as fresh swap — Alpine bindings need to be initialized,
        // scripts need to be re-executed.
        this._initAlpineSubtree(contentEl);
        this._activateRevealItems(contentEl);
        this._reexecuteScripts(contentEl);
        document.dispatchEvent(new CustomEvent('tm:supermarket-grid:content-swapped', {
          bubbles: true,
          detail: { root: contentEl, sectionId: this.sectionId },
        }));
      } else if (this.collectionsWrapperEl) {
        this.collectionsWrapperEl.innerHTML = html;
      }
    }

    // Force the theme's scroll-reveal items visible. Cards carry
    // `anm-reveal-item` which is opacity:0.01 until data-active="true".
    // The theme's observer only watches `.anm-reveal-container` parents,
    // which our grid doesn't use, so without this the cards stay invisible.
    _activateRevealItems(root) {
      if (!root) return;
      const items = root.querySelectorAll('.anm-reveal-item');
      items.forEach((el) => {
        el.dataset.active = 'true';
        el.classList.add('animation-card');
      });
      if (items.length) {
        console.log('[TM-tab] activated', items.length, 'reveal items (made visible)');
      }
    }

    // Initialize Alpine bindings on a freshly-injected subtree.
    // Without this, x-data/x-bind/x-on attributes added via innerHTML
    // never bootstrap, and x-cloak keeps content hidden.
    //
    // CRITICAL double-add fix: the theme runs a global MutationObserver that
    // auto-initializes nodes added to the DOM. When we set innerHTML AND then
    // also call Alpine.initTree() on the same subtree, components whose
    // x-data root already got picked up by the observer get initialized a
    // SECOND time — producing two xProductForm scopes on one form, hence two
    // submit handlers and "add 2 to cart". We therefore only initialize roots
    // that are NOT already initialized (no _x_dataStack), mirroring the
    // theme's own guard in cart-drawer.js.
    _initAlpineSubtree(root) {
      if (!root) return;
      try {
        if (window.Alpine && typeof window.Alpine.initTree === 'function') {
          // Defer to the next microtask so the theme's MutationObserver has a
          // chance to claim the new nodes first. Then only init anything it
          // missed (still lacking _x_dataStack).
          window.Alpine.nextTick(function () {
            var roots = root.querySelectorAll('[x-data]');
            var initializedCount = 0;
            roots.forEach(function (el) {
              if (!el._x_dataStack) {
                window.Alpine.initTree(el);
                initializedCount++;
              }
            });
            console.log('[TM-tab] Alpine init: initialized', initializedCount, 'uninitialized roots (skipped already-bound)');
          });
        } else {
          console.warn('[TM-tab] Alpine global not found — bindings may not init');
        }
      } catch (err) {
        console.error('[TM-tab] Alpine init failed:', err);
      }
    }

    // <script> tags added via innerHTML are inert (the browser does not
    // execute them). Recreate each one so it runs. We skip scripts we
    // already know are loaded globally to avoid double-execution.
    _reexecuteScripts(root) {
      if (!root) return;
      const scripts = root.querySelectorAll('script');
      scripts.forEach((oldScript) => {
        // Skip if same src already exists in document head (loaded once already)
        if (oldScript.src) {
          const existing = document.querySelector(`script[src="${oldScript.src}"]`);
          if (existing && existing !== oldScript) return;
        }
        const newScript = document.createElement('script');
        for (const attr of oldScript.attributes) {
          newScript.setAttribute(attr.name, attr.value);
        }
        newScript.textContent = oldScript.textContent;
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    }
  }

  if (!customElements.get("custom-collections")) {
    customElements.define("custom-collections", CustomCollections);
  }
}
